import { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import * as api from '../../api';
import { Button, Card } from '../../components/ui';
import { currentEndpoint, disablePush, enablePush, pushSupported } from '../../state/push';
import { useActiveMemberId } from '../../state/activeMember';

/**
 * Per-device push toggle. Notifications are granted per browser+device, so this reflects and
 * controls THIS device only — the wall tablet and each phone each opt in separately.
 */
export function NotificationsPanel() {
  const activeId = useActiveMemberId();
  const supported = pushSupported();
  const [subscribed, setSubscribed] = useState<boolean | null>(null);
  const [note, setNote] = useState<string | null>(null);

  useEffect(() => {
    if (!supported) return;
    void (async () => {
      const endpoint = await currentEndpoint();
      if (!endpoint) {
        setSubscribed(false);
        return;
      }
      const { subscribed } = await api.getPushStatus(endpoint);
      setSubscribed(subscribed);
    })();
  }, [supported]);

  const enable = useMutation({
    mutationFn: () => enablePush(activeId),
    onSuccess: (result) => {
      if (result === 'ok') {
        setSubscribed(true);
        setNote('This device will now get reminders.');
      } else if (result === 'denied') {
        setNote('Notifications are blocked in this browser — allow them in site settings, then try again.');
      } else {
        setNote('Push isn’t available in this browser.');
      }
    },
    onError: (e: Error) => setNote(e.message),
  });

  const disable = useMutation({
    mutationFn: disablePush,
    onSuccess: () => {
      setSubscribed(false);
      setNote(null);
    },
  });

  const test = useMutation({
    mutationFn: api.testPush,
    onSuccess: (r) => setNote(r.sent > 0 ? 'Test sent — check your notifications.' : 'No devices are subscribed yet.'),
  });

  return (
    <section className="flex flex-col gap-3">
      <h2 className="font-display text-[20px] text-primary">Notifications</h2>
      <p className="text-[13px] leading-relaxed text-secondary">
        Get a push reminder before events, even when Pacer is closed. Set a reminder time on any
        event. Turn it on per device — the wall and each phone opt in separately.
      </p>

      <Card className="flex flex-col gap-3 p-4">
        {!supported ? (
          <p className="font-mono text-[12px] text-muted">
            This browser doesn’t support push notifications. On iPhone, add Pacer to your home
            screen first, then open it from there.
          </p>
        ) : subscribed ? (
          <>
            <div className="flex items-center gap-3">
              <span className="size-2 rounded-full bg-success" />
              <span className="flex-1 text-[14px] text-primary">Reminders are on for this device</span>
              <Button variant="ghost" size="sm" onClick={() => disable.mutate()} disabled={disable.isPending}>
                Turn off
              </Button>
            </div>
            <Button variant="secondary" size="sm" onClick={() => test.mutate()} disabled={test.isPending} className="self-start">
              Send a test
            </Button>
          </>
        ) : (
          <Button
            variant="primary"
            icon="bell"
            onClick={() => enable.mutate()}
            disabled={enable.isPending}
            className="self-start"
          >
            {enable.isPending ? 'Enabling…' : 'Turn on reminders'}
          </Button>
        )}

        {note && <p className="font-mono text-[12px] leading-relaxed text-muted">{note}</p>}
      </Card>
    </section>
  );
}
